// Client-side search, filter, sort and paging for the admin members table. The usage
// endpoint returns every member for the month in one response (one row per user), so the
// table works on that list in the browser and never refetches for these controls.
import type { BudgetState, MemberUsage, PageInfo } from '@scribe/core/types';

export type MemberSortKey =
  | 'member'
  | 'recordings'
  | 'batch'
  | 'streaming'
  | 'storage'
  | 'tokens'
  | 'cost'
  | 'budget'
  | 'state';

export type SortDirection = 'asc' | 'desc';

export type BudgetStateFilter = 'all' | BudgetState;

/** `active` = any recording, transcription, storage, AI use or cost this month. */
export type ActivityFilter = 'all' | 'active' | 'inactive';

export interface MemberTableQuery {
  search: string;
  state: BudgetStateFilter;
  activity: ActivityFilter;
  sortKey: MemberSortKey;
  sortDirection: SortDirection;
  page: number;
  pageSize: number;
}

export const PAGE_SIZES = [25, 50, 100] as const;

export const DEFAULT_MEMBER_TABLE_QUERY: MemberTableQuery = {
  search: '',
  state: 'all',
  activity: 'all',
  sortKey: 'member',
  sortDirection: 'asc',
  page: 1,
  pageSize: 25,
};

const STATE_RANK: Record<BudgetState, number> = { ok: 0, warn: 1, exceeded: 2 };

export function isActive(m: MemberUsage): boolean {
  return (
    m.recordings_count > 0 ||
    m.batch_seconds > 0 ||
    m.streaming_seconds > 0 ||
    m.storage_bytes > 0 ||
    m.ai_tokens_in + m.ai_tokens_out > 0 ||
    m.estimated_cost_usd > 0
  );
}

/** Numbers and strings to compare; `null` (an uncapped budget) always sorts last. */
function sortValue(m: MemberUsage, key: MemberSortKey): string | number | null {
  switch (key) {
    case 'member':
      return (m.display_name || m.email).toLocaleLowerCase();
    case 'recordings':
      return m.recordings_count;
    case 'batch':
      return m.batch_seconds;
    case 'streaming':
      return m.streaming_seconds;
    case 'storage':
      return m.storage_bytes;
    case 'tokens':
      return m.ai_tokens_in + m.ai_tokens_out;
    case 'cost':
      return m.estimated_cost_usd;
    case 'budget':
      return m.monthly_budget_usd ?? null;
    case 'state':
      return STATE_RANK[m.budget_state];
  }
}

export function filterMembers(
  members: readonly MemberUsage[],
  { search, state, activity }: Pick<MemberTableQuery, 'search' | 'state' | 'activity'>,
): MemberUsage[] {
  const needle = search.trim().toLocaleLowerCase();
  return members.filter(
    (m) =>
      (needle === '' ||
        m.display_name.toLocaleLowerCase().includes(needle) ||
        m.email.toLocaleLowerCase().includes(needle)) &&
      (state === 'all' || m.budget_state === state) &&
      (activity === 'all' || (activity === 'active') === isActive(m)),
  );
}

export function sortMembers(
  members: readonly MemberUsage[],
  key: MemberSortKey,
  direction: SortDirection,
): MemberUsage[] {
  const sign = direction === 'asc' ? 1 : -1;
  return members
    .map((m, i) => ({ m, i, v: sortValue(m, key) }))
    .sort((a, b) => {
      if (a.v === null || b.v === null) {
        if (a.v === b.v) return a.i - b.i;
        return a.v === null ? 1 : -1; // uncapped last, whichever direction
      }
      const cmp =
        typeof a.v === 'string' && typeof b.v === 'string'
          ? a.v.localeCompare(b.v)
          : (a.v as number) - (b.v as number);
      return cmp !== 0 ? cmp * sign : a.i - b.i; // stable: ties keep the server's order
    })
    .map(({ m }) => m);
}

/** One page of `members` plus the `PageInfo` the shared `Pagination` control reads. A page
 * past the end clamps to the last page (e.g. after a filter shrinks the list). */
export function pageMembers(
  members: readonly MemberUsage[],
  page: number,
  pageSize: number,
): { items: MemberUsage[]; pageInfo: PageInfo } {
  const total = members.length;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(1, page), lastPage);
  const start = (current - 1) * pageSize;
  return {
    items: members.slice(start, start + pageSize),
    pageInfo: { page: current, page_size: pageSize, total, has_more: current < lastPage },
  };
}

export function applyMemberTableQuery(members: readonly MemberUsage[], q: MemberTableQuery) {
  const filtered = filterMembers(members, q);
  const sorted = sortMembers(filtered, q.sortKey, q.sortDirection);
  return { ...pageMembers(sorted, q.page, q.pageSize), matched: filtered.length };
}
